<template>
  <router-link
    :to="to"
    :disabled="disabled"
    :target="target"
  >
    <b-button
      :class="buttonClasses"
      :disabled="disabled"
      :size="computedSize"
      :variant="color"
      v-bind="$attrs"
      :target="target"
      v-on="$listeners"
    >
      <slot>{{ text }}</slot>
    </b-button>
  </router-link>
</template>

<script>
export default {
  name: 'T2Btn',
  inheritAttrs: false,
  props: {
    text: {
      type: String,
      default: '',
    },
    to: {
      type: [String, Object],
      default: '#',
    },
    target: {
      type: String,
      default: '_self',
      validator: value => ['_self', '_blank', '_parent', '_top'].includes(value),
    },
    type: {
      type: String,
      default: 'fill',
      validator: value => ['fill', 'outline', 'ghost'].includes(value),
    },
    color: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'secondary', 'success', 'danger', 'warning', 'info'].includes(value),
    },
    size: {
      type: [String, Number],
      default: 40,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    buttonClasses() {
      return [
        't2-btn',
        `t2-btn-${this.type}`,
        `t2-btn-${this.color}`,
        `t2-btn-size-${this.size}`,
        {
          't2-btn-disabled': this.disabled,
          't2-btn-full-width': this.fullWidth,
        },
      ]
    },
    computedSize() {
      // Return null to override Bootstrap-Vue default sizing
      return null
    },
  },
}
</script>

<style lang="scss" scoped>

.t2-btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border: none;
  font-weight: 500;
  line-height: normal;
  text-align: center;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;

  &:focus {
    box-shadow: none;
    outline: none;
  }

  &:active {
    transform: translateY(1px);
  }

  // Size variations
  &.t2-btn-size-40 {
    height: 40px;
    padding: 19px 48px;
    border-radius: 24px;
    font-size: 16px;
  }

  &.t2-btn-size-36 {
    height: 36px;
    padding: 16px 40px;
    border-radius: 20px;
    font-size: 14px;
  }

  &.t2-btn-size-32 {
    height: 32px;
    padding: 12px 32px;
    border-radius: 18px;
    font-size: 14px;
  }

  &.t2-btn-size-28 {
    height: 28px;
    padding: 8px 24px;
    border-radius: 16px;
    font-size: 12px;
  }

  // Type variations
  &.t2-btn-fill {
    &.t2-btn-primary {
      background: $primary;
      color: #060606;

      &:hover:not(.t2-btn-disabled) {
        background: darken($primary, 10%);
        color: #060606;
      }

      &:active:not(.t2-btn-disabled) {
        background: darken($primary, 15%);
      }
    }

    &.t2-btn-secondary {
      background: #6c757d;
      color: #fff;

      &:hover:not(.t2-btn-disabled) {
        background: darken(#6c757d, 10%);
        color: #fff;
      }
    }

    &.t2-btn-success {
      background: #28a745;
      color: #fff;

      &:hover:not(.t2-btn-disabled) {
        background: darken(#28a745, 10%);
        color: #fff;
      }
    }

    &.t2-btn-danger {
      background: #dc3545;
      color: #fff;

      &:hover:not(.t2-btn-disabled) {
        background: darken(#dc3545, 10%);
        color: #fff;
      }
    }

    &.t2-btn-warning {
      background: #ffc107;
      color: #060606;

      &:hover:not(.t2-btn-disabled) {
        background: darken(#ffc107, 10%);
        color: #060606;
      }
    }

    &.t2-btn-info {
      background: #17a2b8;
      color: #fff;

      &:hover:not(.t2-btn-disabled) {
        background: darken(#17a2b8, 10%);
        color: #fff;
      }
    }
  }

  &.t2-btn-outline {
    background: transparent;
    border: 2px solid;

    &.t2-btn-primary {
      border-color: $primary;
      color: $primary;

      &:hover:not(.t2-btn-disabled) {
        background: $primary;
        color: #060606;
      }
    }

    &.t2-btn-secondary {
      border-color: #6c757d;
      color: #6c757d;

      &:hover:not(.t2-btn-disabled) {
        background: #6c757d;
        color: #fff;
      }
    }

    &.t2-btn-success {
      border-color: #28a745;
      color: #28a745;

      &:hover:not(.t2-btn-disabled) {
        background: #28a745;
        color: #fff;
      }
    }

    &.t2-btn-danger {
      border-color: #dc3545;
      color: #dc3545;

      &:hover:not(.t2-btn-disabled) {
        background: #dc3545;
        color: #fff;
      }
    }

    &.t2-btn-warning {
      border-color: #ffc107;
      color: #ffc107;

      &:hover:not(.t2-btn-disabled) {
        background: #ffc107;
        color: #060606;
      }
    }

    &.t2-btn-info {
      border-color: #17a2b8;
      color: #17a2b8;

      &:hover:not(.t2-btn-disabled) {
        background: #17a2b8;
        color: #fff;
      }
    }
  }

  &.t2-btn-ghost {
    background: transparent;
    border: none;

    &.t2-btn-primary {
      color: $primary;

      &:hover:not(.t2-btn-disabled) {
        background: rgba($primary, 0.1);
      }
    }

    &.t2-btn-secondary {
      color: #6c757d;

      &:hover:not(.t2-btn-disabled) {
        background: rgba(#6c757d, 0.1);
      }
    }

    &.t2-btn-success {
      color: #28a745;

      &:hover:not(.t2-btn-disabled) {
        background: rgba(#28a745, 0.1);
      }
    }

    &.t2-btn-danger {
      color: #dc3545;

      &:hover:not(.t2-btn-disabled) {
        background: rgba(#dc3545, 0.1);
      }
    }

    &.t2-btn-warning {
      color: #ffc107;

      &:hover:not(.t2-btn-disabled) {
        background: rgba(#ffc107, 0.1);
      }
    }

    &.t2-btn-info {
      color: #17a2b8;

      &:hover:not(.t2-btn-disabled) {
        background: rgba(#17a2b8, 0.1);
      }
    }
  }

  // Disabled state
  &.t2-btn-disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      transform: none;
    }
  }

  // Full width
  &.t2-btn-full-width {
    width: 100%;
  }
}

// Responsive design
@media (max-width: 768px) {
  .t2-btn {
    &.t2-btn-size-40 {
      height: 36px;
      padding: 16px 40px;
      font-size: 14px;
    }

    &.t2-btn-size-36 {
      height: 32px;
      padding: 12px 32px;
      font-size: 13px;
    }

    &.t2-btn-size-32 {
      height: 28px;
      padding: 8px 24px;
      font-size: 12px;
    }
  }
}

@media (max-width: 576px) {
  .t2-btn {
    &.t2-btn-size-40 {
      height: 32px;
      padding: 12px 32px;
      font-size: 13px;
    }

    &.t2-btn-size-36 {
      height: 28px;
      padding: 8px 24px;
      font-size: 12px;
    }

    &.t2-btn-size-32 {
      height: 24px;
      padding: 6px 20px;
      font-size: 11px;
    }
  }
}
</style>
